import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import { Orchard } from '../../services/sprayService';

interface Props {
  orchards: Orchard[];
  selected: string[];
  onChange: (ids: string[]) => void;
  singleSelect?: boolean;
}

function formatSecondary(o: Orchard): string {
  const parts: string[] = [];
  if (o.variety) parts.push(o.variety);
  if (o.cultivar) parts.push(o.cultivar);
  if (o.areaHectares != null) parts.push(`${o.areaHectares.toFixed(1)} ha`);
  return parts.join(' \u00b7 ');
}

const OrchardSelector: React.FC<Props> = ({ orchards, selected, onChange, singleSelect = false }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filtered = useMemo(() => {
    if (!search.trim()) return orchards;
    const term = search.trim().toLowerCase();
    return orchards.filter(o =>
      o.orchardName.toLowerCase().includes(term) ||
      (o.variety?.toLowerCase().includes(term)) ||
      (o.cultivar?.toLowerCase().includes(term))
    );
  }, [orchards, search]);

  const toggle = useCallback((id: string) => {
    if (singleSelect) {
      onChange(selectedSet.has(id) ? [] : [id]);
      setOpen(false);
      return;
    }
    if (selectedSet.has(id)) {
      onChange(selected.filter(s => s !== id));
    } else {
      onChange([...selected, id]);
    }
  }, [singleSelect, selected, selectedSet, onChange]);

  const filteredIds = useMemo(() => filtered.map(o => o._id), [filtered]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every(id => selectedSet.has(id));

  const toggleAllFiltered = useCallback(() => {
    if (allFilteredSelected) {
      onChange(selected.filter(id => !filteredIds.includes(id)));
    } else {
      const merged = new Set(selected);
      filteredIds.forEach(id => merged.add(id));
      onChange(Array.from(merged));
    }
  }, [allFilteredSelected, selected, filteredIds, onChange]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }} onKeyDown={handleKeyDown}>
      <Form.Control
        type="text"
        placeholder={t('spray.sessions.searchOrchardsPlaceholder')}
        value={search}
        onChange={e => setSearch(e.target.value)}
        onFocus={() => setOpen(true)}
        aria-expanded={open}
        aria-controls="orchard-dropdown"
      />
      {open && (
        <div
          id="orchard-dropdown"
          data-testid="orchard-dropdown"
          className="border rounded shadow-sm bg-white"
          style={{
            position: 'absolute',
            zIndex: 1050,
            width: '100%',
            maxHeight: 250,
            overflowY: 'auto',
          }}
        >
          {!singleSelect && filtered.length > 0 && (
            <div className="px-2 py-1 border-bottom">
              <Button
                variant="link"
                size="sm"
                className="p-0 text-decoration-none"
                onClick={toggleAllFiltered}
              >
                {allFilteredSelected
                  ? t('spray.sessions.deselectAllMatching')
                  : t('spray.sessions.selectAllMatching')}
              </Button>
            </div>
          )}
          {filtered.length === 0 && (
            <div className="px-2 py-2 text-muted">
              {t('spray.sessions.noOrchardsMatch')}
            </div>
          )}
          {filtered.map(o => (
            <div
              key={o._id}
              className="d-flex align-items-center px-2 py-1 border-bottom"
              style={{ cursor: 'pointer', minHeight: 44 }}
              onClick={() => toggle(o._id)}
              id={singleSelect ? `orchard-opt-${o._id}` : undefined}
              role={singleSelect ? 'option' : undefined}
              aria-selected={singleSelect ? selectedSet.has(o._id) : undefined}
            >
              {!singleSelect && (
                <Form.Check
                  type="checkbox"
                  checked={selectedSet.has(o._id)}
                  onChange={() => toggle(o._id)}
                  onClick={e => e.stopPropagation()}
                  className="me-2"
                  id={`orchard-opt-${o._id}`}
                />
              )}
              <div>
                <div className={singleSelect && selectedSet.has(o._id) ? 'fw-bold' : undefined}>
                  {o.orchardName}
                </div>
                {(() => { const sec = formatSecondary(o); return sec ? <small className="text-muted">{sec}</small> : null; })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrchardSelector;
